// Line chart controller
// Usage:
// <div
//   data-controller="line-chart"
//   data-line-chart-dataset-value='[["2023-01-25", 180], ["2023-01-26", 51]]'>
// </div>
//
// An optional third column draws a dashed "Forecast" series (see Stoked::Forecast):
//   [["Oct 29", 9, 9], ["Oct 30", null, 14]]

import { Controller } from "@hotwired/stimulus"
import { ensureApexCharts } from "../utils/apex_charts"

function computeNiceYAxis(maxValue) {
  if (maxValue <= 0) return { max: 4, tickAmount: 4 }
  const padded = maxValue * 1.25
  const intervals = [1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, 2500, 5000]
  for (const interval of intervals) {
    const max = Math.ceil(padded / interval) * interval
    const ticks = max / interval
    if (ticks >= 2 && ticks <= 5) return { max, tickAmount: ticks }
  }
  return { max: Math.ceil(padded), tickAmount: 4 }
}

// Show only the series with a value at the hovered point, and drop a forecast
// that just repeats the actual (yesterday's bridge point). Today shows both.
function withoutEmptySeries(props) {
  const { dataPointIndex, ctx } = props
  const valueAt = (i) => props.series[i][dataPointIndex]
  const shown = ctx.opts.series
    .map((_, i) => i)
    .filter((i) => valueAt(i) != null)
    .filter((i, n, visible) => visible.findIndex((j) => valueAt(j) === valueAt(i)) === n)

  return {
    ...props,
    series: shown.map((i) => props.series[i]),
    ctx: {
      ...ctx,
      opts: {
        ...ctx.opts,
        series: shown.map((i) => ctx.opts.series[i]),
        colors: shown.map((i) => ctx.opts.colors[i])
      }
    }
  }
}

export default class extends Controller {
  static values = {
    dataset: Array
  }

  async connect() {
    if (!window.Stoked) return
    if (!this.hasDatasetValue || this.datasetValue.length === 0) return

    // ApexCharts and Preline's chart helper are code-split into on-demand chunks;
    // load them before buildChart (which does `new ApexCharts`) runs. Bail if
    // Stimulus tore the controller down while the chunk was in flight.
    const { buildChart, buildTooltip } = await ensureApexCharts()
    if (!this.element?.isConnected) return

    const labels = this.datasetValue.map(([label]) => label)
    const values = this.datasetValue.map(([, value]) => value)
    const forecasts = this.datasetValue.map(([, , forecast]) => forecast ?? null)
    const hasForecast = forecasts.some((forecast) => forecast !== null)
    const maxValue = Math.max(...values, ...forecasts)
    const series = [{ name: "Value", type: "area", data: values }]
    if (hasForecast) series.push({ name: "Forecast", type: "line", data: forecasts })
    const { max: yAxisMax, tickAmount } = computeNiceYAxis(maxValue)

    const selector = `#${this.element.id}`

    buildChart(
      selector,
      (mode) => ({
        chart: {
          height: 300,
          type: "area",
          toolbar: {
            show: false
          },
          zoom: {
            enabled: false
          }
        },
        series,
        legend: {
          show: false
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: "straight",
          width: 2,
          dashArray: [0, 5]
        },
        grid: {
          strokeDashArray: 2
        },
        fill: {
          type: ["gradient", "solid"],
          gradient: {
            type: "vertical",
            shadeIntensity: 1,
            opacityFrom: 0.1,
            opacityTo: 0.8
          }
        },
        xaxis: {
          type: "category",
          tickPlacement: "on",
          categories: labels,
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          },
          crosshairs: {
            stroke: {
              dashArray: 0
            },
            dropShadow: {
              show: false
            }
          },
          tooltip: {
            enabled: false
          },
          labels: {
            style: {
              colors: '#9ca3af',
              fontSize: '13px',
              fontFamily: 'inherit',
              fontWeight: 400
            }
          }
        },
        yaxis: {
          min: 0,
          max: yAxisMax,
          tickAmount,
          labels: {
            align: "left",
            minWidth: 0,
            maxWidth: 140,
            style: {
              colors: "#9ca3af",
              fontSize: "13px",
              fontFamily: "inherit",
              fontWeight: 400
            },
            decimalsInFloat: 0,
            formatter: (value) => (value >= 1000 ? `${value / 1000}k` : Math.round(value))
          }
        },
        tooltip: {
          x: {
            format: "MMMM yyyy"
          },
          y: {
            formatter: (value) =>
              `${value >= 1000 ? `${value / 1000}k` : value}`
          },
          custom: function (props) {
            const { categories } = props.ctx.opts.xaxis
            const { dataPointIndex } = props
            const title = categories[dataPointIndex]?.toString().split(" ")
            const newTitle =
              title && title.length >= 2 ? `${title[0]} ${title[1]}` : categories[dataPointIndex]

            return buildTooltip(withoutEmptySeries(props), {
              title: newTitle,
              mode,
              valuePrefix: "",
              hasTextLabel: true,
              markerExtClasses: "rounded-xs!",
              wrapperExtClasses: "min-w-28"
            })
          }
        },
        responsive: [
          {
            breakpoint: 568,
            options: {
              chart: {
                height: 300
              },
              labels: {
                style: {
                  colors: "#9ca3af",
                  fontSize: "11px",
                  fontFamily: "Inter, ui-sans-serif",
                  fontWeight: 400
                },
                offsetX: -2
              },
              yaxis: {
                min: 0,
                max: yAxisMax,
                tickAmount,
                labels: {
                  align: "left",
                  minWidth: 0,
                  maxWidth: 140,
                  style: {
                    colors: "#9ca3af",
                    fontSize: "11px",
                    fontFamily: "Inter, ui-sans-serif",
                    fontWeight: 400
                  },
                  decimalsInFloat: 0,
                  formatter: (value) =>
                    value >= 1000 ? `${value / 1000}k` : Math.round(value)
                }
              }
            }
          }
        ]
      }),
      {
        colors: ["#2563eb", "#2563eb"],
        fill: {
          gradient: {
            stops: [0, 90, 100]
          }
        },
        grid: {
          borderColor: "#e5e7eb"
        }
      },
      {
        colors: ["#3b82f6", "#3b82f6"],
        fill: {
          gradient: {
            stops: [100, 90, 0]
          }
        },
        grid: {
          borderColor: "#404040"
        }
      }
    )
  }

  disconnect() {
    // ApexCharts instances are managed by Preline's buildChart helper
  }
}


